import * as React from "react";
import { Content } from "@iventis/translations/content/typed-content";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { DataField } from "@iventis/domain-model/model/dataField";
import { Autocomplete, Paper, TextField } from "@mui/material";
import { Body2, StyledFieldLabelLight, inlineTextIconMargin, inputMarginBottom, styled } from "@iventis/styles";
import { filterDataFieldListOptionsByRelationships, isValidUuid } from "@iventis/utilities";

import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterFormat } from "@iventis/types";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { useDataFieldServices } from "@iventis/datafield-editor/src/data-fields-services";
import { LIST_ITEMS_QUERY_KEY } from "@iventis/datafield-editor/src/data-fields-types.constants";
import { MULTIPLE_VALUES_LABEL } from "./object-details-constants";

// Use this multiple value when there are multiple values that are not the same.
const defaultMultipleValue = { id: MULTIPLE_VALUES_LABEL, name: MULTIPLE_VALUES_LABEL } as DataFieldListItem;

export const ListDataFieldSelector: React.FunctionComponent<{
    dataField: DataField;
    listItemIds: string[]; // Can be multiple values if multiple map objects are selected.
    onChange: (listItemId: string) => void;
    disabled?: boolean;
    relationshipValues: Record<string, unknown[]>;
}> = ({ dataField, listItemIds, onChange, disabled, relationshipValues }) => {
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const [listItemSelectorOpened, setListItemSelectorOpened] = React.useState<boolean>(false);
    const dfService = useDataFieldServices();
    const translate = useIventisTranslate();
    const queryClient = useQueryClient();

    // The value will either be the id of a list item, or the id of the default MULTIPLE value.
    const listItemId = useMemo(() => {
        const allValuesIdentical = listItemIds.every((value) => value === listItemIds[0]);
        return allValuesIdentical ? listItemIds[0] : MULTIPLE_VALUES_LABEL;
    }, [listItemIds]);

    // Request to get the list item options, only once the list item selector has been opened.
    const { data: listItemOptions, isLoading: listItemOptionsLoading, fetchStatus: listItemOptionFetchStatus } = useQuery(
        [LIST_ITEMS_QUERY_KEY, dataField.id],
        async () => dfService.dataFieldListItemsService.getDataFieldListItems(dataField.id),
        {
            refetchOnWindowFocus: false,
            enabled: listItemSelectorOpened,
        }
    );

    const listValueFilter: FilterFormat[] = [
        {
            fieldName: "Id",
            $type: "IventisFilterGuidArray",
            operator: IventisFilterOperator.In,
            value: [listItemId],
        },
    ];

    // Request to the list item value. Only happens when the value is not null or empty.
    const { data: listItemValue, fetchStatus: listItemValueFetchStatus } = useQuery(
        [LIST_ITEMS_QUERY_KEY, dataField.id, listValueFilter],
        async () => {
            if (!isValidUuid(listItemId)) {
                return null;
            }
            if (listItemId === MULTIPLE_VALUES_LABEL) {
                return defaultMultipleValue;
            }
            // If the listItem options are invalidated, we don't want to use it to get the list item because it may be incorrect
            const listItemOptionsInvalidated = queryClient.getQueryState([LIST_ITEMS_QUERY_KEY, dataField.id])?.isInvalidated;
            return (
                (listItemOptionsInvalidated ? undefined : listItemOptions?.find((l) => l.id === listItemId)) ??
                dfService.dataFieldListItemsService.getDataFieldListItems(dataField.id, listValueFilter).then((value) => (value.length === 1 ? value[0] : null))
            );
        },
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true, // Prevents a flicking in the autcomplete when you select a different option.
        }
    );

    const isExpandable = dataField.listItemProperties?.length > 0;

    // List options either need to include. The options should include the value when the options have not been loaded otherwise autocomplete throws and invalid console warning.
    const getListOptionItems = () => {
        if (listItemOptionsLoading && listItemOptionFetchStatus !== "idle") {
            return [];
        }
        if (listItemOptions != null) {
            return filterDataFieldListOptionsByRelationships(listItemOptions, dataField, relationshipValues);
        }

        if (listItemValue != null) {
            return [listItemValue];
        }
        return [];
    };

    return (
        <>
            <StyledFieldLabelLight className="object-data-field-label">{dataField.name}</StyledFieldLabelLight>
            <StyledInputContainer>
                {isExpandable && (
                    <StyledFontAwesomeIcon
                        icon={["fas", expanded ? "caret-down" : "caret-right"]}
                        onClick={() => setExpanded(!expanded)}
                        data-testid={`expand-properties-${dataField.name}`}
                    />
                )}
                <StyledValueContainer>
                    <Autocomplete<DataFieldListItem, false, true, false>
                        options={getListOptionItems()}
                        value={listItemValue ?? null}
                        loading={listItemOptionsLoading}
                        disableClearable
                        disabled={disabled}
                        onChange={(e, selection: DataFieldListItem) => onChange(selection.id)}
                        onOpen={() => setListItemSelectorOpened(true)}
                        getOptionLabel={(listItem: DataFieldListItem) => listItem.name}
                        isOptionEqualToValue={(opt, value) => opt.id === value.id}
                        autoHighlight
                        renderInput={(params) => (
                            <>
                                <StyledTextFieldInput
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...params}
                                    variant="outlined"
                                    data-testid={`attribute-value-${dataField.name}`}
                                    InputProps={{
                                        ...params.InputProps,
                                        type: "text",
                                        "aria-label": "textbox",
                                        ...(listItemValueFetchStatus !== "idle" &&
                                            listItemValue == null && {
                                                endAdornment: <FontAwesomeIcon icon={["fas", "circle-notch"]} spin style={{ marginRight: inlineTextIconMargin }} />,
                                            }),
                                    }}
                                />
                                {expanded && (
                                    <StyledPropertiesPaper elevation={3}>
                                        <BodyProp>
                                            <strong>{translate(Content.map6.list_item_properties.additional_properties)}</strong>
                                        </BodyProp>
                                        {dataField.listItemProperties.map((p) => {
                                            const propName = p.name;
                                            const prop = listItemValue?.propertyValues?.find((pv) => pv.propertyId === p.id);
                                            const propValue = prop ? prop?.number ?? prop?.text : "";
                                            return (
                                                <BodyProp key={p.id} data-testid={`${propName}:${propValue}`}>
                                                    <strong>{propName}</strong>: {propValue}
                                                </BodyProp>
                                            );
                                        })}
                                    </StyledPropertiesPaper>
                                )}
                            </>
                        )}
                    />
                </StyledValueContainer>
            </StyledInputContainer>
        </>
    );
};

const StyledValueContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const BodyProp = styled(Body2)`
    margin-right: 10px;
    padding: 5px;
`;

const StyledPropertiesPaper = styled(Paper)`
    margin-bottom: 10px;
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
    margin-top: ${inputMarginBottom};
    align-self: flex-start;
    cursor: pointer;
    min-width: 16px;
`;

const StyledInputContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
`;

const StyledTextFieldInput = styled(TextField)`
    .MuiInputBase-root {
        background-color: ${(props) => props.theme.shades.five};
    }
`;
